import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type User } from "./api";

type Status = "checking" | "in" | "out";

/**
 * Session state. It starts at "checking" rather than "out": showing the login
 * form while the check is in flight would flash it at anyone with a valid
 * session on every load.
 */
export function useSession() {
  const [status, setStatus] = useState<Status>("checking");
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    let live = true;
    api
      .me()
      .then((u) => {
        if (live) {
          setUser(u);
          setStatus("in");
        }
      })
      .catch(() => {
        if (live) setStatus("out");
      });
    return () => {
      live = false;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const u = await api.login(email, password);
    setUser(u);
    setStatus("in");
  }, []);

  /** Adopts a session the server already created (passkey login). */
  const adopt = useCallback((u: User) => {
    setUser(u);
    setStatus("in");
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      // Even if logout fails server-side we sign out locally: leaving the UI as
      // though you were still in is worse than an orphaned token that expires
      // on its own anyway.
      setUser(null);
      setStatus("out");
    }
  }, []);

  /** So any 401 from a later call sends the person back to the login screen. */
  const handleAuthError = useCallback((err: unknown) => {
    if (err instanceof ApiError && err.status === 401) {
      setUser(null);
      setStatus("out");
      return true;
    }
    return false;
  }, []);

  return { status, user, login, logout, adopt, handleAuthError };
}
